/**
 * M6 — Raktár oldal
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { get, post, patch } from '../../lib/api';
import { PageHeader, Button, Badge, Card, Input, Modal, EmptyState, Tabs } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';
import { useFeature } from '../../hooks/useFeature';
import LockedFeature from '../../components/LockedFeature';

// ── Típusok ────────────────────────────────────────────────────────────────────

interface Warehouse { id: string; name: string; isActive: boolean; createdAt: string; }
interface StockLevel {
  itemId: string; itemName: string; itemSku: string | null;
  quantity: number; minStockQuantity: number | null; allowNegative: boolean;
  fifoValue: number; avgUnitCost: number | null; belowMinimum: boolean;
}
interface StockMovement {
  id: string; type: 'BEVET' | 'KIAD' | 'KORREKCIO'; quantity: number;
  unitCostHuf: number | null; source: string; sourceId: string | null;
  itemId: string; itemName: string; note: string | null; createdAt: string;
}
interface Item { id: string; name: string; sku: string | null; unit: string | null; }

// ── Segédfüggvények ────────────────────────────────────────────────────────────

const TYPE_LABEL: Record<string, string> = { BEVET: 'Bevételezés', KIAD: 'Kiadás', KORREKCIO: 'Korrekció' };
const TYPE_COLOR: Record<string, string> = { BEVET: 'success', KIAD: 'error', KORREKCIO: 'info' };
const SOURCE_LABEL: Record<string, string> = {
  KEZI: 'Kézi', BESZERZES: 'Beszerzés', SZALLITOLEVEL: 'Szállítólevél',
  SZAMLA: 'Számla', LELTAR: 'Leltár',
};

function fmtNum(n: number) { return n.toLocaleString('hu-HU', { minimumFractionDigits: 0, maximumFractionDigits: 2 }); }

// ── Fő komponens ───────────────────────────────────────────────────────────────

export default function WarehousePage() {
  const { active, loading: featureLoading } = useFeature('raktar');
  if (featureLoading) return <div className="py-16 text-center text-gray-400">Betöltés...</div>;
  if (!active) return <LockedFeature feature="raktar" />;
  return <WarehouseContent />;
}

function WarehouseContent() {
  const { toast } = useToast();
  const [warehouse, setWarehouse] = useState<Warehouse | null>(null);
  const [stock, setStock] = useState<StockLevel[]>([]);
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [movTotal, setMovTotal] = useState(0);
  const [movOffset, setMovOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showReceive, setShowReceive] = useState(false);
  const [showAdjust, setShowAdjust] = useState(false);
  const [selectedItem, setSelectedItem] = useState<StockLevel | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsItem, setSettingsItem] = useState<StockLevel | null>(null);
  const MOV_LIMIT = 30;

  const loadAll = useCallback(async (wh?: Warehouse) => {
    try {
      const w = wh ?? warehouse;
      if (!w) return;
      const [s, m] = await Promise.all([
        get<StockLevel[]>(`/warehouses/${w.id}/stock`),
        get<{ movements: StockMovement[]; total: number }>(`/warehouses/${w.id}/movements?limit=${MOV_LIMIT}&offset=0`),
      ]);
      setStock(Array.isArray(s) ? s : []);
      setMovements(m.movements || []);
      setMovTotal(m.total || 0);
      setMovOffset(0);
    } catch { /* silent */ }
  }, [warehouse]);

  const loadMovePage = useCallback(async (offset: number) => {
    if (!warehouse) return;
    try {
      const m = await get<{ movements: StockMovement[]; total: number }>(`/warehouses/${warehouse.id}/movements?limit=${MOV_LIMIT}&offset=${offset}`);
      setMovements(m.movements || []);
      setMovOffset(offset);
    } catch { /* silent */ }
  }, [warehouse]);

  useEffect(() => {
    setLoading(true);
    get<Warehouse>('/warehouses/default')
      .then(w => { setWarehouse(w); return Promise.all([
        get<StockLevel[]>(`/warehouses/${w.id}/stock`),
        get<{ movements: StockMovement[]; total: number }>(`/warehouses/${w.id}/movements?limit=${MOV_LIMIT}&offset=0`),
      ]).then(([s, m]) => { setStock(Array.isArray(s) ? s : []); setMovements(m.movements || []); setMovTotal(m.total || 0); }); })
      .catch(() => toast('Raktár betöltési hiba.', 'error'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-16 text-center text-gray-400">Betöltés...</div>;
  if (!warehouse) return <div className="py-16 text-center text-gray-400">Nincs elérhető raktár.</div>;

  const filteredStock = stock.filter(s =>
    s.itemName.toLowerCase().includes(search.toLowerCase()) ||
    (s.itemSku || '').toLowerCase().includes(search.toLowerCase()),
  );

  const totalValue = stock.reduce((s, i) => s + i.fifoValue, 0);
  const belowMin = stock.filter(i => i.belowMinimum).length;

  return (
    <div>
      <PageHeader
        title={`Raktár — ${warehouse.name}`}
        description={`${stock.length} cikk · Készletérték: ${totalValue.toLocaleString('hu-HU')} Ft`}
        actions={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => { setSelectedItem(null); setShowAdjust(true); }}>Leltárkorrekció</Button>
            <Button onClick={() => { setSelectedItem(null); setShowReceive(true); }}>+ Bevételezés</Button>
          </div>
        }
      />

      {belowMin > 0 && (
        <div className="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800 flex items-center gap-2">
          <span className="font-semibold">⚠ {belowMin} cikk minimumkészlet alatt</span>
        </div>
      )}

      <Tabs tabs={[
        {
          label: 'Készletszintek',
          content: (
            <div className="space-y-4">
              <Input placeholder="Keresés cikknév, SKU..." value={search} onChange={e => setSearch(e.target.value)} />
              {filteredStock.length === 0 ? (
                <EmptyState title="Üres raktár" description="Még nincs bevételezett cikk. Kezdj bevételezéssel!" />
              ) : (
                <Card>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[600px]">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Cikk</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Mennyiség</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Átl. bekerülési ár</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">FIFO érték</th>
                          <th className="px-4 py-3" />
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStock.map(item => (
                          <tr key={item.itemId} className="border-b border-gray-50 hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="font-medium text-[#1a2540]">{item.itemName}</div>
                              {item.itemSku && <div className="text-xs text-gray-400">{item.itemSku}</div>}
                              {item.belowMinimum && (
                                <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                                  ⚠ Min: {fmtNum(item.minStockQuantity!)}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className={`font-semibold ${item.quantity <= 0 ? 'text-red-600' : 'text-[#1a2540]'}`}>
                                {fmtNum(item.quantity)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right text-gray-600 hidden md:table-cell">
                              {item.avgUnitCost ? `${item.avgUnitCost.toLocaleString('hu-HU')} Ft` : '—'}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-[#1a2540]">
                              {item.fifoValue.toLocaleString('hu-HU')} Ft
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center gap-1 justify-end opacity-0 group-hover:opacity-100">
                                <button onClick={() => { setSelectedItem(item); setShowReceive(true); }}
                                  className="text-xs px-2 py-1 rounded border border-gray-200 hover:bg-gray-100">+Be</button>
                                <button onClick={() => { setSettingsItem(item); setShowSettings(true); }}
                                  className="text-xs px-2 py-1 rounded border border-gray-200 hover:bg-gray-100">⚙</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-gray-200">
                          <td className="px-4 py-3 font-semibold" colSpan={3}>Összesen ({stock.length} cikk)</td>
                          <td className="px-4 py-3 text-right font-bold text-[#F47B20]">{totalValue.toLocaleString('hu-HU')} Ft</td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </Card>
              )}
            </div>
          ),
        },
        {
          label: 'Mozgásnapló',
          content: (
            <div className="space-y-4">
              {movements.length === 0 ? (
                <EmptyState title="Nincs mozgás" description="Még nem történt raktári mozgás." />
              ) : (
                <>
                  <Card>
                    <div className="divide-y divide-gray-100">
                      {movements.map(m => (
                        <div key={m.id} className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 text-sm">
                          <div className="shrink-0 pt-0.5">
                            <Badge variant={TYPE_COLOR[m.type] as 'success' | 'error' | 'info'}>{TYPE_LABEL[m.type]}</Badge>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-[#1a2540] truncate">{m.itemName}</div>
                            <div className="text-xs text-gray-400 flex flex-wrap gap-2 mt-0.5">
                              <span>{SOURCE_LABEL[m.source] || m.source}</span>
                              {m.note && <span>· {m.note}</span>}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className={`font-semibold ${m.type === 'BEVET' ? 'text-green-600' : m.type === 'KIAD' ? 'text-red-600' : 'text-blue-600'}`}>
                              {m.type === 'BEVET' ? '+' : m.type === 'KIAD' ? '−' : '±'}{fmtNum(Math.abs(m.quantity))}
                            </div>
                            {m.unitCostHuf !== null && (
                              <div className="text-xs text-gray-400">{m.unitCostHuf.toLocaleString('hu-HU')} Ft/db</div>
                            )}
                            <div className="text-xs text-gray-400">{new Date(m.createdAt).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                  {movTotal > MOV_LIMIT && (
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span>{movOffset + 1}–{Math.min(movOffset + MOV_LIMIT, movTotal)} / {movTotal}</span>
                      <div className="flex gap-2">
                        <Button size="sm" variant="secondary" disabled={movOffset === 0} onClick={() => loadMovePage(movOffset - MOV_LIMIT)}>← Előző</Button>
                        <Button size="sm" variant="secondary" disabled={movOffset + MOV_LIMIT >= movTotal} onClick={() => loadMovePage(movOffset + MOV_LIMIT)}>Következő →</Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          ),
        },
      ]} />

      {showReceive && (
        <ReceiveModal
          isOpen={showReceive}
          onClose={() => { setShowReceive(false); setSelectedItem(null); }}
          warehouseId={warehouse.id}
          preselectedItem={selectedItem}
          onSuccess={() => { setShowReceive(false); setSelectedItem(null); loadAll(); }}
        />
      )}
      {showAdjust && (
        <AdjustModal
          isOpen={showAdjust}
          onClose={() => { setShowAdjust(false); setSelectedItem(null); }}
          warehouseId={warehouse.id}
          stock={stock}
          preselectedItem={selectedItem}
          onSuccess={() => { setShowAdjust(false); setSelectedItem(null); loadAll(); }}
        />
      )}
      {showSettings && settingsItem && (
        <StockSettingsModal
          isOpen={showSettings}
          onClose={() => { setShowSettings(false); setSettingsItem(null); }}
          item={settingsItem}
          onSuccess={() => { setShowSettings(false); setSettingsItem(null); loadAll(); }}
        />
      )}
    </div>
  );
}

// ── Bevételezés modal ──────────────────────────────────────────────────────────

function ReceiveModal({ isOpen, onClose, warehouseId, preselectedItem, onSuccess }: {
  isOpen: boolean; onClose: () => void; warehouseId: string;
  preselectedItem: StockLevel | null; onSuccess: () => void;
}) {
  const { toast } = useToast();
  const [items, setItems] = useState<Item[]>([]);
  const [itemId, setItemId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    get<{ items: Item[] }>('/items?pageSize=500').then(d => setItems(d.items || [])).catch(() => {});
    setItemId(preselectedItem?.itemId || '');
    setQuantity(''); setUnitCost(''); setNote('');
  }, [isOpen, preselectedItem]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!itemId || !quantity) { toast('Cikk és mennyiség kötelező.', 'warning'); return; }
    setSaving(true);
    try {
      await post(`/warehouses/${warehouseId}/receive`, {
        itemId, quantity: parseFloat(quantity), unitCostHuf: parseFloat(unitCost || '0'), note: note || null,
      });
      toast('Bevételezve.', 'success');
      onSuccess();
    } catch (e: any) { toast(e?.message || 'Hiba.', 'error'); }
    setSaving(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Bevételezés">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">Cikk *</label>
          <select value={itemId} onChange={e => setItemId(e.target.value)} required className="form-input">
            <option value="">Válassz cikket...</option>
            {items.map(i => <option key={i.id} value={i.id}>{i.name}{i.sku ? ` (${i.sku})` : ''}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input label="Mennyiség *" type="number" value={quantity} onChange={e => setQuantity(e.target.value)} placeholder="0" required min="0.001" step="any" />
          <Input label="Bekerülési ár (Ft/db)" type="number" value={unitCost} onChange={e => setUnitCost(e.target.value)} placeholder="0" min="0" step="any" />
        </div>
        <Input label="Megjegyzés" value={note} onChange={e => setNote(e.target.value)} placeholder="pl. szállítói számlaszám" />
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" type="button" onClick={onClose}>Mégse</Button>
          <Button type="submit" loading={saving}>Bevételezés</Button>
        </div>
      </form>
    </Modal>
  );
}

// ── Leltárkorrekció modal ──────────────────────────────────────────────────────

function AdjustModal({ isOpen, onClose, warehouseId, stock, preselectedItem, onSuccess }: {
  isOpen: boolean; onClose: () => void; warehouseId: string; stock: StockLevel[];
  preselectedItem: StockLevel | null; onSuccess: () => void;
}) {
  const { toast } = useToast();
  const [itemId, setItemId] = useState('');
  const [newQty, setNewQty] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const currentQty = stock.find(s => s.itemId === itemId)?.quantity ?? null;

  useEffect(() => {
    if (!isOpen) return;
    setItemId(preselectedItem?.itemId || '');
    setNewQty(preselectedItem ? String(preselectedItem.quantity) : '');
    setNote('');
  }, [isOpen, preselectedItem]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!itemId || newQty === '') { toast('Cikk és mennyiség kötelező.', 'warning'); return; }
    setSaving(true);
    try {
      await post(`/warehouses/${warehouseId}/adjust`, { itemId, newQuantity: parseFloat(newQty), note: note || null });
      toast('Leltárkorrekció rögzítve.', 'success');
      onSuccess();
    } catch (e: any) { toast(e?.message || 'Hiba.', 'error'); }
    setSaving(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Leltárkorrekció">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">Cikk *</label>
          <select value={itemId} onChange={e => { setItemId(e.target.value); const s = stock.find(i => i.itemId === e.target.value); setNewQty(s ? String(s.quantity) : ''); }} required className="form-input">
            <option value="">Válassz cikket...</option>
            {stock.map(i => <option key={i.itemId} value={i.itemId}>{i.itemName}</option>)}
          </select>
        </div>
        {currentQty !== null && (
          <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-3">
            Jelenlegi készlet: <strong>{fmtNum(currentQty)}</strong>
          </div>
        )}
        <Input label="Új mennyiség *" type="number" value={newQty} onChange={e => setNewQty(e.target.value)} placeholder="0" required min="0" step="any" />
        {currentQty !== null && newQty !== '' && (
          <div className={`text-sm font-medium ${parseFloat(newQty) > currentQty ? 'text-green-600' : 'text-red-600'}`}>
            Delta: {parseFloat(newQty) > currentQty ? '+' : ''}{fmtNum(parseFloat(newQty) - currentQty)}
          </div>
        )}
        <Input label="Megjegyzés" value={note} onChange={e => setNote(e.target.value)} placeholder="pl. leltár 2026-09" />
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" type="button" onClick={onClose}>Mégse</Button>
          <Button type="submit" loading={saving}>Rögzítés</Button>
        </div>
      </form>
    </Modal>
  );
}

// ── Készletbeállítások modal ───────────────────────────────────────────────────

function StockSettingsModal({ isOpen, onClose, item, onSuccess }: {
  isOpen: boolean; onClose: () => void; item: StockLevel; onSuccess: () => void;
}) {
  const { toast } = useToast();
  const [minQty, setMinQty] = useState('');
  const [allowNeg, setAllowNeg] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setMinQty(item.minStockQuantity !== null ? String(item.minStockQuantity) : '');
    setAllowNeg(item.allowNegative);
  }, [isOpen, item]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await patch(`/warehouses/items/${item.itemId}/stock-settings`, {
        minStockQuantity: minQty !== '' ? parseFloat(minQty) : null,
        allowNegativeStock: allowNeg,
      });
      toast('Beállítások mentve.', 'success');
      onSuccess();
    } catch (e: any) { toast(e?.message || 'Hiba.', 'error'); }
    setSaving(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Készletbeállítások — ${item.itemName}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input label="Minimumkészlet (riasztási szint)" type="number" value={minQty} onChange={e => setMinQty(e.target.value)} placeholder="pl. 10" min="0" step="any" />
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={allowNeg} onChange={e => setAllowNeg(e.target.checked)} />
          <span>Mínuszkészlet engedélyezése <span className="text-gray-400">(alapban tiltva)</span></span>
        </label>
        {allowNeg && (
          <div className="text-xs text-amber-600 bg-amber-50 rounded-lg p-2">
            ⚠ Ha engedélyezed, a kiadás negatív készlet esetén is sikerül — ez jelzálogkészlet-kockázatot jelent.
          </div>
        )}
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" type="button" onClick={onClose}>Mégse</Button>
          <Button type="submit" loading={saving}>Mentés</Button>
        </div>
      </form>
    </Modal>
  );
}
